import { useState, createRef, useEffect } from 'react'
import Cropper, { ReactCropperElement } from 'react-cropper'
import { Box, Button, Divider, useMediaQuery, useTheme } from '@mui/material'

import { HelperWrapper } from '../helper-wrapper'
import { Loader } from '../loader'

import 'cropperjs/dist/cropper.css'
import './cropped-modal.css'
import { Toolbar } from './toolbar.component'

interface IProps {
  imageToCrop: any
  setImage: (image: any) => void
  initialAspectRatio?: string
  handleCloseModal: () => void
}

export const ImageCropper = ({
  imageToCrop,
  setImage: setImageToCrop,
  initialAspectRatio = '0-Custom',
  handleCloseModal,
}: IProps) => {
  const [initialAspectValue, initialAspectLabel] = initialAspectRatio.split('-')

  const [roundMode, setRroundMode] = useState(initialAspectLabel === 'Circle')
  const [image] = useState(imageToCrop?.icon)
  const [isLoading, setIsLoading] = useState(true)

  const cropperRef = createRef<ReactCropperElement>()

  const theme = useTheme()
  const isTablet = useMediaQuery(theme.breakpoints.down('tablet'))

  useEffect(() => {
    if (cropperRef.current?.cropper) {
      cropperRef.current?.cropper.setAspectRatio(Number(initialAspectValue))
    }
  }, [initialAspectValue])

  const getCropData = () => {
    if (typeof cropperRef.current?.cropper !== 'undefined') {
      cropperRef.current?.cropper.getCroppedCanvas({ fillColor: '#ffffff' }).toBlob((blob) => {
        if (blob) {
          const croppedImageFile = new File([blob], 'cropped-image.jpg', {
            type: 'image/jpeg',
            lastModified: Date.now(),
          })

          setImageToCrop({
            file: croppedImageFile,
            icon: cropperRef.current?.cropper.getCroppedCanvas().toDataURL(),
          })
        }
      }, 'image/jpeg')
    }
  }

  return (
    <Box
      sx={{
        position: 'relative',
        width: isTablet ? '90vw' : '100%',
        maxWidth: '600px',
        maxHeight: isTablet ? '70vh' : '80vh',
        height: 'auto',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'start',
        justifyContent: 'start',
      }}
    >
      <Cropper
        className={roundMode ? 'cropper-round-mode' : ''}
        ref={cropperRef}
        style={{
          height: 'auto',
          width: '100%',
          maxWidth: '700px',
          maxHeight: isTablet ? '220px' : '350px',
        }}
        // zoomTo={zoom}
        // zoom={(e) => {
        //   setZoom(e.detail.ratio)
        // }}
        initialAspectRatio={Number(initialAspectValue)}
        src={image}
        ready={() => {
          setIsLoading(false)
        }}
        onLoad={() => {
          setIsLoading(true)
        }}
        onError={() => {
          setIsLoading(false)
        }}
        viewMode={0}
        dragMode={'move'}
        minCropBoxHeight={100}
        minCropBoxWidth={100}
        background={true}
        responsive={true}
        autoCropArea={1}
        checkOrientation={false} // https://github.com/fengyuanchen/cropperjs/issues/671
        guides={true}
      />

      {!isLoading && (
        <>
          <Toolbar
            cropperRef={cropperRef}
            setRroundMode={setRroundMode}
            initialAspectRatio={initialAspectRatio}
          />
          <Divider sx={{ width: '100%' }} />
          <Box
            sx={{
              p: '12px',
              display: 'flex',
              justifyContent: 'center',
              gap: '25px',
              width: '100%',
            }}
          >
            <Button
              disabled={isLoading}
              variant="outlined"
              type="button"
              size="small"
              onClick={(e) => {
                e.preventDefault()

                handleCloseModal()
              }}
            >
              <HelperWrapper t="cancel-button-text" isColorInherited />
            </Button>
            <Button
              disabled={isLoading}
              size="small"
              variant="contained"
              type="button"
              onClick={(e) => {
                e.preventDefault()

                getCropData()
              }}
            >
              <HelperWrapper t="save-button-text" isColorInherited />
            </Button>
          </Box>
        </>
      )}
      {isLoading && <Loader />}
    </Box>
  )
}
